import { BaseElement, css, html, define, upgradeProperties } from '../../../core/index.js';

const TREE_TAG = 'omni-tree';
const BRANCH_TAG = 'omni-tree-branch';

const treeStyles = css`
:host {
  display: block;
  --omni-tree-line: var(--tree-line-color, #d0d7e2);
  --omni-tree-indent: var(--tree-indent, 1rem);
  --omni-tree-expander-size: 1rem;
  --omni-tree-expander-color: var(--tree-expander-color, #4a5568);
  --omni-tree-search-bg: var(--tree-search-bg, #f5f7fb);
  --omni-tree-search-border: var(--tree-search-border, #c8d0de);
  --omni-tree-text: var(--tree-text, #1f2933);
  font-family: inherit;
  color: var(--omni-tree-text);
}

.tree-shell {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.search-row {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  background: var(--omni-tree-search-bg);
  border: 1px solid var(--omni-tree-search-border);
  border-radius: 0.35rem;
  padding: 0.25rem 0.4rem;
}

.search-row[hidden] {
  display: none;
}

.search-row input[type="search"] {
  width: 100%;
  border: none;
  background: transparent;
  font: inherit;
  color: inherit;
  outline: none;
}

::slotted([data-tree-hidden]) {
  display: none !important;
}
`;

const treeTemplate = html`
  <div class="tree-shell" part="shell">
    <label class="search-row" part="search" hidden>
      <input type="search" placeholder="Search" aria-label="Search tree" />
    </label>
    <slot></slot>
  </div>
`;

const branchStyles = css`
:host {
  display: block;
  position: relative;
  --branch-line-color: var(--omni-tree-line, #d0d7e2);
  --branch-border-radius: 0.4rem;
  --branch-header-bg: var(--tree-branch-header-bg, transparent);
  --branch-header-padding: calc(var(--omni-tree-expander-size, 1rem) + 0.2rem);
  --branch-body-offset: var(--branch-header-padding);
  --branch-body-padding: calc(var(--omni-tree-indent, 1rem) * 0.5);
}

.branch {
  border-radius: var(--branch-border-radius);
}

.header-row {
  display: flex;
  align-items: flex-start;
  gap: 0.35rem;
  padding: 0.15rem 0.15rem 0.15rem var(--branch-header-padding);
  cursor: pointer;
  user-select: none;
  position: relative;
}

:host(:not([root])) .header-row {
  padding-left: 0;
}

.header-row:focus-visible {
  outline: 2px solid var(--branch-line-color);
  outline-offset: 2px;
}

.header-row button.expander {
  width: var(--omni-tree-expander-size, 1rem);
  height: var(--omni-tree-expander-size, 1rem);
  border: none;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
}

:host(:not([root])) .header-row button.expander {
  left: calc(var(--branch-header-padding) * -1);
}

.header-row button.expander::before {
  content: '';
  display: inline-block;
  border-style: solid;
  border-width: 0.3rem 0 0.3rem 0.4rem;
  border-color: transparent transparent transparent var(--omni-tree-expander-color, #4a5568);
  transform-origin: center;
  transition: transform 0.2s ease;
}

:host([expanded]) .header-row button.expander::before {
  transform: rotate(90deg);
}

.summary-slot {
  flex: 1;
  background: var(--branch-header-bg);
  min-width: 0;
}

.summary-slot ::slotted(header) {
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  align-items: baseline;
}

.summary-slot ::slotted(header h1),
.summary-slot ::slotted(header h2),
.summary-slot ::slotted(header h3),
.summary-slot ::slotted(header h4),
.summary-slot ::slotted(header h5),
.summary-slot ::slotted(header h6) {
  margin: 0;
}

.body {
  margin-left: var(--branch-body-offset);
  border-left: 1px dashed var(--branch-line-color);
  padding-left: var(--branch-body-padding);
  display: none;
}

.body ::slotted(label) {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.1rem 0;
}

.select-all-slot::slotted(*) {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-weight: inherit;
  padding: 0.1rem 0;
  margin: 0;
}

:host([expanded]) .body {
  display: block;
}

::slotted([data-tree-hidden]) {
  display: none !important;
}

::slotted([data-tree-hidden="true"]) {
  display: none !important;
}

:host([data-hidden-by-filter]) {
  display: none !important;
}
`;

const branchTemplate = html`
  <div class="branch" part="branch">
    <div class="header-row" part="header" tabindex="0" role="button" aria-expanded="false">
      <button class="expander" part="expander" type="button" aria-label="Toggle branch"></button>
      <div class="summary-slot" part="summary">
        <slot name="parent"></slot>
      </div>
    </div>
    <div class="body" part="body">
      <slot name="select-all" class="select-all-slot"></slot>
      <slot></slot>
    </div>
  </div>
`;

class OmniTree extends BaseElement {
  static shadowStyleSheets = [treeStyles];
  static get observedAttributes() {
    return ['searchable', 'search-placeholder'];
  }

  constructor() {
    super();
    upgradeProperties(this, ['searchable', 'searchPlaceholder', 'accordion']);
    this._root.append(treeTemplate.cloneNode(true));
    this._searchRow = this._root.querySelector('.search-row');
    this._searchInput = this._root.querySelector('input[type="search"]');
    this._handleSearchInput = this._handleSearchInput.bind(this);
    this._handleBranchToggle = this._handleBranchToggle.bind(this);
    this._handleCheckboxChange = this._handleCheckboxChange.bind(this);
  }

  connectedCallback() {
    super.connectedCallback?.();
    this._syncSearchVisibility();
    this._syncSearchPlaceholder();
    this._searchInput?.addEventListener('input', this._handleSearchInput);
    this.addEventListener('omni-tree-branch-toggle', this._handleBranchToggle);
    this.addEventListener('change', this._handleCheckboxChange);
  }

  disconnectedCallback() {
    this._searchInput?.removeEventListener('input', this._handleSearchInput);
    this.removeEventListener('omni-tree-branch-toggle', this._handleBranchToggle);
    this.removeEventListener('change', this._handleCheckboxChange);
  }

  attributeChangedCallback(name) {
    super.attributeChangedCallback?.(name);
    if (name === 'searchable') this._syncSearchVisibility();
    if (name === 'search-placeholder') this._syncSearchPlaceholder();
  }

  get searchable() {
    return this.hasAttribute('searchable');
  }

  set searchable(val) {
    val ? this.setAttribute('searchable', '') : this.removeAttribute('searchable');
  }

  get accordion() {
    return this.hasAttribute('accordion');
  }

  set accordion(val) {
    val ? this.setAttribute('accordion', '') : this.removeAttribute('accordion');
  }

  get searchPlaceholder() {
    return this.getAttribute('search-placeholder') ?? 'Search';
  }

  set searchPlaceholder(val) {
    if (val == null) this.removeAttribute('search-placeholder');
    else this.setAttribute('search-placeholder', val);
  }

  _syncSearchVisibility() {
    if (!this._searchRow) return;
    if (this.searchable) this._searchRow.hidden = false;
    else {
      this._searchRow.hidden = true;
      this._searchInput.value = '';
      this._applyFilter('');
    }
  }

  _syncSearchPlaceholder() {
    if (!this._searchInput) return;
    this._searchInput.placeholder = this.searchPlaceholder;
  }

  _handleSearchInput(event) {
    const query = event.currentTarget?.value ?? '';
    this._applyFilter(query);
  }

  _handleBranchToggle(event) {
    if (!this.accordion) return;
    const { branch, expanded } = event.detail ?? {};
    if (!branch || !expanded) return;
    const parentBranch = branch._parentBranch ?? null;
    const siblings = parentBranch ? parentBranch._getChildBranches() : this._getRootBranches();
    siblings.forEach((sib) => {
      if (sib === branch) return;
      sib.collapse({ silent: true });
    });
  }

  _handleCheckboxChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'checkbox') return;
    if (target.dataset.branchSummary === 'true') return;
    this.dispatchEvent(new CustomEvent('selectionchange', {
      detail: { values: this.getCheckedValues() }
    }));
  }

  _applyFilter(query) {
    const text = (query ?? '').trim().toLowerCase();
    const branches = this._getRootBranches();
    let anyMatch = false;
    branches.forEach((branch) => {
      if (typeof branch.applyFilter !== 'function') return;
      const match = branch.applyFilter(text);
      branch.toggleAttribute('data-hidden-by-filter', !!text && !match);
      if (match) anyMatch = true;
    });
    this.toggleAttribute('data-filtering', !!text);
    return anyMatch;
  }

  _getRootBranches() {
    return Array.from(this.children).filter((el) => el.tagName === BRANCH_TAG.toUpperCase());
  }

  getCheckedValues() {
    const inputs = this.querySelectorAll('input[type="checkbox"]');
    const values = [];
    inputs.forEach((input) => {
      if (input.dataset.branchSummary === 'true') return;
      if (input.checked) values.push(input.value ?? 'on');
    });
    return values;
  }
}

class OmniTreeBranch extends BaseElement {
  static shadowStyleSheets = [branchStyles];
  static get observedAttributes() {
    return ['expanded', 'label'];
  }

  constructor() {
    super();
    upgradeProperties(this, ['expanded']);
    this._root.append(branchTemplate.cloneNode(true));
    this._expander = this._root.querySelector('.expander');
    this._headerRow = this._root.querySelector('.header-row');
    this._body = this._root.querySelector('.body');
    this._parentSlot = this._root.querySelector('slot[name="parent"]');
    this._selectAllSlot = this._root.querySelector('slot[name="select-all"]');
    this._bodySlot = this._root.querySelector('slot:not([name])');
    this._onHeaderKeyDown = this._onHeaderKeyDown.bind(this);
    this._onHeaderClick = this._onHeaderClick.bind(this);
    this._onChange = this._onChange.bind(this);
    this._expander.addEventListener('click', () => this.toggle());
    this._headerRow.addEventListener('keydown', this._onHeaderKeyDown);
    this._headerRow.addEventListener('click', this._onHeaderClick);
    this._parentSlot?.addEventListener('slotchange', () => this._captureParent());
    this._selectAllSlot?.addEventListener('slotchange', () => this._captureSelectAll());
    this._bodySlot?.addEventListener('slotchange', () => {
      this._captureParent();
      this._updateSummaryState();
    });
  }

  connectedCallback() {
    super.connectedCallback?.();
    this._captureParent();
    this._captureSelectAll();
    this._computeDepth();
    this._reflectExpanded();
    this.addEventListener('change', this._onChange);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this._onChange);
  }

  attributeChangedCallback(name) {
    super.attributeChangedCallback?.(name);
    if (name === 'expanded') this._reflectExpanded();
    if (name === 'label') this._captureParent();
  }

  get expanded() {
    return this.hasAttribute('expanded');
  }

  set expanded(val) {
    val ? this.setAttribute('expanded', '') : this.removeAttribute('expanded');
  }

  toggle(options) {
    if (this.expanded) this.collapse(options);
    else this.expand(options);
  }

  expand(options) {
    if (this.expanded) return;
    this.expanded = true;
    this._emitToggle(options);
  }

  collapse(options) {
    if (!this.expanded) return;
    this.expanded = false;
    this._collapseDescendants();
    if (!options?.silent) {
      this.dispatchEvent(new CustomEvent('omni-tree-branch-toggle', {
        bubbles: true,
        composed: true,
        detail: { branch: this, expanded: false, depth: this._depth }
      }));
    }
  }

  _emitToggle(options) {
    if (options?.silent) return;
    this.dispatchEvent(new CustomEvent('omni-tree-branch-toggle', {
      bubbles: true,
      composed: true,
      detail: { branch: this, expanded: true, depth: this._depth }
    }));
  }

  _reflectExpanded() {
    this._headerRow?.setAttribute('aria-expanded', String(this.expanded));
    this.toggleAttribute('expanded', this.expanded);
  }

  _captureParent() {
    let parentEl = this._findExplicitParentElement();
    const hasLabelAttribute = this.hasAttribute('label');
    const labelText = this.getAttribute('label') ?? '';

    if (!parentEl && hasLabelAttribute) {
      parentEl = this._ensureGeneratedParent(labelText || 'Group');
    }

    if (!parentEl) {
      parentEl = this._findLegacyHeaderElement();
    }

    if (!parentEl) {
      parentEl = this._findImplicitParentElement();
    }

    if (!parentEl) {
      parentEl = this._ensureGeneratedParent('Group');
    } else if (this._generatedParent && this._generatedParent !== parentEl) {
      this._generatedParent.remove();
      this._generatedParent = null;
    }

    if (parentEl && parentEl.slot !== 'parent') parentEl.slot = 'parent';

    this._headerEl = parentEl;
    this._syncHeaderLabel();
    this._syncSummarySource();
    this._normalizeHeaderHeadings();
  }

  _findExplicitParentElement() {
    const assigned = this._parentSlot?.assignedElements({ flatten: true }) ?? [];
    if (assigned.length) return assigned[0];
    return Array.from(this.children).find((child) => (
      child.getAttribute?.('slot') === 'parent' ||
      child.getAttribute?.('slot') === 'header' ||
      child.getAttribute?.('slot') === 'summary'
    )) ?? null;
  }

  _findLegacyHeaderElement() {
    return Array.from(this.children).find((child) => child.tagName === 'HEADER' && !child.hasAttribute('slot')) ?? null;
  }

  _findImplicitParentElement() {
    return Array.from(this.children).find((child) => this._isParentCandidate(child)) ?? null;
  }

  _isParentCandidate(child) {
    if (!(child instanceof HTMLElement)) return false;
    if (child === this._generatedParent) return false;
    if (child.hasAttribute('slot')) return false;
    if (child.tagName === BRANCH_TAG.toUpperCase()) return false;
    return child.tagName === 'LABEL' || child.hasAttribute('data-tree-parent');
  }

  _ensureGeneratedParent(labelText) {
    if (!this._generatedParent) {
      const label = document.createElement('label');
      const input = document.createElement('input');
      input.type = 'checkbox';
      label.append(input, document.createTextNode(labelText ? ` ${labelText}` : ' Group'));
      label.slot = 'parent';
      label.dataset.generatedParent = 'true';
      this.prepend(label);
      this._generatedParent = label;
    } else {
      const textNode = Array.from(this._generatedParent.childNodes).find((node) => node.nodeType === Node.TEXT_NODE);
      if (textNode) textNode.textContent = labelText ? ` ${labelText}` : ' Group';
    }
    return this._generatedParent;
  }

  _syncHeaderLabel() {
    const labelText = this.getAttribute('label') ?? '';
    if (labelText && this._headerEl && this._headerEl !== this._generatedParent) {
      this._headerRow?.setAttribute('aria-label', labelText);
      this._headerRow?.setAttribute('title', labelText);
    } else {
      this._headerRow?.removeAttribute('aria-label');
      this._headerRow?.removeAttribute('title');
    }
  }

  _captureSelectAll() {
    const nodes = this._selectAllSlot?.assignedElements({ flatten: true }) ?? [];
    this._selectAllEl = nodes[0] ?? null;
    this._syncSummarySource();
  }

  _onHeaderKeyDown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      const isCheckbox = event.composedPath().some((node) => node instanceof HTMLInputElement && node.type === 'checkbox');
      if (isCheckbox) return;
      event.preventDefault();
      this.toggle();
    }
  }

  _onHeaderClick(event) {
    const path = event.composedPath();
    if (path.some((node) => node instanceof HTMLInputElement && node.type === 'checkbox')) return;
    if (event.target.closest('button.expander')) return;
    this.toggle();
  }

  _onChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'checkbox') return;
    if (this._bulkUpdating && target !== this._summaryCheckbox) return;
    if (target === this._summaryCheckbox) {
      this._setDescendantChecked(target.checked);
    }
    this._updateSummaryState();
    this._notifyAncestors();
  }

  _syncSummarySource() {
    const previousSummary = this._summaryCheckbox;
    const nextSummary = this._findSummaryCheckbox();
    if (previousSummary && previousSummary !== nextSummary) {
      delete previousSummary.dataset.branchSummary;
      previousSummary.indeterminate = false;
    }
    this._summaryCheckbox = nextSummary ?? null;
    if (this._summaryCheckbox) {
      this._summaryCheckbox.dataset.branchSummary = 'true';
    }
    this._updateSummaryState();
  }

  _findSummaryCheckbox() {
    const headerInput = this._queryCheckboxWithin(this._headerEl);
    if (headerInput) return headerInput;
    const selectAllInput = this._queryCheckboxWithin(this._selectAllEl);
    if (selectAllInput) return selectAllInput;
    return null;
  }

  _queryCheckboxWithin(container) {
    if (!container) return null;
    if (container instanceof HTMLInputElement && container.type === 'checkbox') {
      return container;
    }
    if (container instanceof HTMLElement) {
      return container.querySelector('input[type="checkbox"]');
    }
    return null;
  }

  _setDescendantChecked(checked) {
    const inputs = this.querySelectorAll('input[type="checkbox"]');
    this._bulkUpdating = true;
    inputs.forEach((input) => {
      if (input === this._summaryCheckbox) return;
      if (input.dataset.branchSummary === 'true') return;
      if (input.checked === checked) return;
      input.checked = checked;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    this._bulkUpdating = false;
  }

  _updateSummaryState() {
    if (!this._summaryCheckbox) return;
    const inputs = Array.from(this.querySelectorAll('input[type="checkbox"]')).filter((input) => input.dataset.branchSummary !== 'true');
    if (inputs.length === 0) {
      this._summaryCheckbox.indeterminate = false;
      return;
    }
    const checked = inputs.filter((input) => input.checked);
    if (checked.length === 0) {
      this._summaryCheckbox.checked = false;
      this._summaryCheckbox.indeterminate = false;
    } else if (checked.length === inputs.length) {
      this._summaryCheckbox.checked = true;
      this._summaryCheckbox.indeterminate = false;
    } else {
      this._summaryCheckbox.checked = false;
      this._summaryCheckbox.indeterminate = true;
    }
  }

  _notifyAncestors() {
    let parent = this._parentBranch ?? this._getParentBranch();
    while (parent) {
      parent._updateSummaryState();
      parent = parent._parentBranch ?? parent._getParentBranch();
    }
  }

  _normalizeHeaderHeadings() {
    if (!this._headerEl) return;
    const headings = this._headerEl.querySelectorAll('h1, h2, h3, h4, h5, h6');
    headings.forEach((heading) => {
      if (!heading.dataset.omniTreeHeadingReset) {
        heading.dataset.omniTreeHeadingReset = 'true';
        heading.style.marginTop = '0';
        heading.style.marginBottom = '0';
      }
    });
  }

  _getParentBranch() {
    let parent = this.parentElement;
    while (parent) {
      if (parent.tagName === BRANCH_TAG.toUpperCase()) return parent;
      parent = parent.parentElement;
    }
    return null;
  }

  _computeDepth() {
    let depth = 0;
    this._parentBranch = this._getParentBranch();
    let parent = this._parentBranch;
    while (parent) {
      depth += 1;
      parent = parent._parentBranch ?? parent._getParentBranch?.();
    }
    this._depth = depth;
    this.style.setProperty('--branch-depth', depth);
    if (depth === 0) this.setAttribute('root', '');
    else this.removeAttribute('root');
  }

  applyFilter(query) {
    const text = (query ?? '').trim().toLowerCase();
    const matchAll = text.length === 0;
    if (!matchAll && this._preFilterExpanded == null) {
      this._preFilterExpanded = this.expanded;
    } else if (matchAll && this._preFilterExpanded != null) {
      this.expanded = this._preFilterExpanded;
      this._preFilterExpanded = null;
    }

    let hasMatch = false;
    const leaves = this._getDirectLeafElements();
    leaves.forEach((leaf) => {
      const leafText = leaf.textContent?.toLowerCase() ?? '';
      const matches = matchAll || leafText.includes(text);
      leaf.toggleAttribute('data-tree-hidden', !matches);
      if (matches) hasMatch = true;
    });

    const childBranches = this._getChildBranches();
    childBranches.forEach((branch) => {
      const childMatch = typeof branch.applyFilter === 'function' ? branch.applyFilter(text) : false;
      branch.toggleAttribute('data-hidden-by-filter', !matchAll && !childMatch);
      if (childMatch) hasMatch = true;
    });

    if (!matchAll) {
      if (hasMatch) {
        this.expand({ silent: true });
      }
      this.toggleAttribute('data-hidden-by-filter', !hasMatch);
    } else {
      this.toggleAttribute('data-hidden-by-filter', false);
      leaves.forEach((leaf) => leaf.removeAttribute('data-tree-hidden'));
    }

    return hasMatch;
  }

  _getDirectLeafElements() {
    const nodes = this._bodySlot?.assignedElements({ flatten: true }) ?? [];
    return nodes.filter((node) => this._isLeafElement(node));
  }

  _getChildBranches() {
    const nodes = this._bodySlot?.assignedElements({ flatten: true }) ?? [];
    return nodes.filter((node) => node.tagName === BRANCH_TAG.toUpperCase());
  }

  _collapseDescendants() {
    const children = this._getChildBranches();
    children.forEach((child) => child.collapse({ silent: true }));
  }

  _isLeafElement(node) {
    if (!(node instanceof HTMLElement)) return false;
    if (node.tagName === 'LABEL') {
      return !!node.querySelector('input[type="checkbox"]');
    }
    if (node.hasAttribute('data-tree-leaf')) return true;
    return false;
  }
}

define(TREE_TAG, OmniTree);
define(BRANCH_TAG, OmniTreeBranch);

export { OmniTree, OmniTreeBranch };
